import { z } from 'zod';

// Report Studio foundation (story 4-113) — the versioned, declarative
// `ReportPresentationSchema` the story's own "Data/architecture" paragraph
// specifies: `{ schemaVersion, document, header, footer, sections, blocks,
// stylePresetId? }`, references keyed by stable template section/field ids,
// unknown properties rejected, missing configs resolved through documented
// defaults, migrations pure/versioned/independently tested.
//
// ── Deliberately scoped, judgment call recorded here ────────────────────────
// This module is CLIENT-SIDE ONLY for now — nothing here round-trips to the
// backend as a new persisted field/entity. Reasoning: none of the eight
// sibling tool-group stories (4-114 Document…4-120 Style) have landed, so
// there is no concrete per-section/header/footer/block property yet that
// NEEDS server persistence beyond what `ReportLayout` (`schema.report`,
// unaffected by this file) already saves. `document`/`header`/`footer`/
// `SectionPresentation` below are real, strict, versioned, TESTED scaffolds
// — not stubs — but they are intentionally EMPTY of concrete fields today:
// each tool-group story adds its own optional fields via `.extend()` on the
// matching object when it actually has something to store, which is a
// backward-compatible addition under `.strict()` (existing stored documents
// simply lack the new optional key; `.strict()` only rejects a key the
// schema does NOT declare, not a declared-but-absent one), so no version
// bump is needed for a purely additive field. A schema version bump is
// reserved for an actually incompatible shape change. `blocks` (`ReportBlock`)
// keeps the one property (`id`+`type`) any future story needs to reference a
// block at all — no tool group populates real block types yet either.
//
// `sections` is genuinely exercised today, though: `pruneOrphanedSections`
// below is real, tested logic the editor calls on every schema change, and
// `stylePresetId` is wired to the EXISTING saved-style mechanism
// (`useReportStudio.ts`) the moment a template author applies or saves one —
// not just scaffolding with no caller.

export const REPORT_PRESENTATION_SCHEMA_VERSION = 1 as const;

// Every one of these strict objects is where a later tool-group story adds
// its own fields (4-114 -> document, 4-115 -> header/footer, 4-116 ->
// section). `.strict()` from day one means a typo'd or removed property is
// rejected at validation time instead of silently ignored, matching this
// repo's own backend zod-contract convention (CLAUDE.md).
//
// Each shape is built in its own named variable before `z.object(...)`
// rather than an inline object literal — not a style preference, the
// pattern this repo's own G-26 lint rule names as the sanctioned shape for
// a hand-declared schema outside the generated-contract regime (this file
// is not a form, but the same "build the shape in a variable first" escape
// hatch applies verbatim).
const emptyShape = {};
const DocumentPresentationSchema = z.object(emptyShape).strict();
const HeaderPresentationSchema = z.object(emptyShape).strict();
const FooterPresentationSchema = z.object(emptyShape).strict();

// Story 4-116 — Section Presentation Tools: the nine real fields this
// scaffold was left empty for (see this file's own header comment above).
// This is a VALIDATION/documented-defaults twin of `agents/shared/
// formSchema.js#resolveSectionPresentation` (the canonical resolver every
// renderer actually reads through) — NOT the persisted store. Deliberate
// judgment call, recorded here: `4-113`'s own foundation left `presentation`
// (this file's top-level `ReportPresentationSchemaV1.sections` map)
// genuinely unpersisted — "the first tool-group story that needs a real
// per-section property to survive a reload decides where it is saved" (that
// file's own header comment). This story's real per-section fields are
// SECTION-scoped report-output properties, exactly like `pageBreak`/
// `hidden`/`reportOrder` already are (`4-110`/`4-114`'s own precedent) — so
// they are persisted the SAME way, as additive fields directly on the
// template's `FormSection` (`section.presentation`, part of `FormSchemaV2`,
// saved through the existing autosave pipeline), not inside this overlay.
// This schema still earns its place: `reportStudioCommands.ts`'s new
// `setSectionPresentationProperty` command validates every patch against it
// before writing to `schema.sections`, so "real zod-equivalent validation
// and documented defaults" (this story's own instruction) is satisfied on
// the actual persisted path, not a parallel unused one.
// Built WITHOUT `.default()` deliberately: this raw shape backs
// `SectionPresentationPatchSchema` below, which validates a PATCH (one or a
// few fields at a time) — a zod `.default()` fires whenever a key is
// missing from the parsed input, which for a partial patch would silently
// fill in every UNTOUCHED field with its default and overwrite whatever the
// section already had stored for it. `SectionPresentationSchema` (the
// documented-defaults, fully-resolved shape `reportPresentationSchema.ts`'s
// own header comment promises) wraps each of these in `.default()`
// separately, immediately below.
// Story 4-118 — Table Tools' 3 more fields, `kind:'table'` sections only.
// Same shape/rationale as `agents/shared/formSchema.js#resolveSectionPresentation`'s
// own header comment (this file's twin, see that comment for every value).
const tableColumnConfigShape = {
  key: z.string(),
  visible: z.boolean().default(true),
  emphasis: z.enum(['standard', 'primary', 'secondary', 'compact']).default('standard'),
};
const TableColumnConfigSchema = z.object(tableColumnConfigShape).strict();

const sectionPresentationRawShape = {
  layout: z.enum(['list', 'groupedList', 'table', 'summaryCards', 'narrative']),
  columns: z.enum(['one', 'two']),
  density: z.enum(['comfortable', 'compact', 'condensed']),
  heading: z.enum(['visible', 'hidden', 'renamed', 'numbered']),
  headingLabel: z.string().nullable(),
  repeatHeading: z.boolean(),
  showEmpty: z.enum(['show', 'hide', 'notProvided']),
  evidencePlacement: z.enum(['inline', 'afterResponse', 'gallery', 'appendix']),
  findingEmphasis: z.enum(['standard', 'highlighted', 'summaryOnly']),
  tableColumns: z.array(TableColumnConfigSchema).nullable(),
  overflow: z.enum(['wrap', 'truncate']),
  showTotalsRow: z.boolean(),
};
const sectionPresentationShape = {
  layout: sectionPresentationRawShape.layout.default('list'),
  columns: sectionPresentationRawShape.columns.default('one'),
  density: sectionPresentationRawShape.density.default('comfortable'),
  heading: sectionPresentationRawShape.heading.default('visible'),
  headingLabel: sectionPresentationRawShape.headingLabel.default(null),
  repeatHeading: sectionPresentationRawShape.repeatHeading.default(false),
  showEmpty: sectionPresentationRawShape.showEmpty.default('show'),
  evidencePlacement: sectionPresentationRawShape.evidencePlacement.default('inline'),
  findingEmphasis: sectionPresentationRawShape.findingEmphasis.default('standard'),
  tableColumns: sectionPresentationRawShape.tableColumns.default(null),
  overflow: sectionPresentationRawShape.overflow.default('wrap'),
  showTotalsRow: sectionPresentationRawShape.showTotalsRow.default(false),
};
const SectionPresentationSchema = z.object(sectionPresentationShape).strict();
/** A single property's patch — `reportStudioCommands.ts#setSectionPresentationProperty`
 * validates one-or-a-few fields at a time against this partial. Built from
 * the RAW (default-free) shape above: an untouched field is simply ABSENT
 * from the validated result, not filled in with its default — a patch
 * merges over the section's existing `presentation`, it does not replace it. */
export const SectionPresentationPatchSchema = z.object(sectionPresentationRawShape).partial().strict();

const reportBlockShape = {
  id: z.string(),
  type: z.string(),
};
const ReportBlockSchema = z.object(reportBlockShape).strict();

const reportPresentationShape = {
  schemaVersion: z.literal(REPORT_PRESENTATION_SCHEMA_VERSION),
  document: DocumentPresentationSchema,
  header: HeaderPresentationSchema,
  footer: FooterPresentationSchema,
  sections: z.record(z.string(), SectionPresentationSchema),
  blocks: z.array(ReportBlockSchema),
  stylePresetId: z.string().nullable().optional(),
};
export const ReportPresentationSchemaV1 = z.object(reportPresentationShape).strict();

export type ReportPresentation = z.infer<typeof ReportPresentationSchemaV1>;
export type SectionPresentation = z.infer<typeof SectionPresentationSchema>;
export type ReportBlock = z.infer<typeof ReportBlockSchema>;

export function defaultReportPresentation(): ReportPresentation {
  return {
    schemaVersion: REPORT_PRESENTATION_SCHEMA_VERSION,
    document: {},
    header: {},
    footer: {},
    sections: {},
    blocks: [],
    stylePresetId: null,
  };
}

export interface ReportPresentationValidation {
  ok: boolean;
  value: ReportPresentation;
  errors: string[];
}

/** Rejects unknown properties (every object above is `.strict()`) and any
 * value that does not match the declared shape; never throws. */
export function validateReportPresentation(input: unknown): ReportPresentationValidation {
  const parsed = ReportPresentationSchemaV1.safeParse(input);
  if (parsed.success) return { ok: true, value: parsed.data, errors: [] };
  return {
    ok: false,
    value: defaultReportPresentation(),
    errors: parsed.error.issues.map((issue) => `${issue.path.join('.') || '(root)'}: ${issue.message}`),
  };
}

/**
 * Pure, versioned migration — "imported older configs contain unknown
 * variants" / "missing configs resolve through documented defaults" (this
 * story's own edge cases). This is not a hypothetical: EVERY template saved
 * before this story has no `reportPresentation` at all, so `undefined` is
 * the real, currently-occurring input this function's main branch handles,
 * not a fabricated legacy format. A `schemaVersion` this build does not
 * recognise (from a future release, or corrupted data) resolves through
 * defaults rather than guessing at an upgrade path that does not exist yet
 * — there has never been a version other than 1.
 */
export function migrateReportPresentation(input: unknown): ReportPresentation {
  if (input === null || input === undefined || typeof input !== 'object') {
    return defaultReportPresentation();
  }
  const raw = input as Record<string, unknown>;
  if (raw.schemaVersion === REPORT_PRESENTATION_SCHEMA_VERSION) {
    const result = validateReportPresentation(raw);
    return result.value;
  }
  // No `schemaVersion` (pre-Report-Studio) or an unrecognised one — both
  // resolve to fresh v1 defaults; there is nothing to carry forward from an
  // unversioned or unknown shape.
  return defaultReportPresentation();
}

/** Documented defaults for the (unpersisted — see this file's own header
 * comment above `sectionPresentationRawShape`) overlay record's own entry
 * shape. `SectionPresentationSchema.parse({})` rather than a hand-written
 * literal so this can never drift from the zod defaults above. */
export function defaultSectionPresentation(): SectionPresentation {
  return SectionPresentationSchema.parse({});
}

export function sectionPresentationOf(
  presentation: ReportPresentation,
  sectionId: string,
): SectionPresentation {
  return presentation.sections[sectionId] ?? defaultSectionPresentation();
}

/**
 * Edge case: "a referenced section/field was deleted from the template" /
 * "a restored template version has different section ids" — drops
 * presentation entries for section ids the template no longer has, so a
 * template that loses or swaps sections cannot accumulate config for a
 * ghost id forever. Pure and referentially stable: returns the SAME object
 * when nothing needed dropping, so a caller in a `useEffect` can skip a
 * re-render when there is nothing to prune.
 */
export function pruneOrphanedSections(
  presentation: ReportPresentation,
  liveSectionIds: ReadonlySet<string>,
): ReportPresentation {
  let changed = false;
  const sections: Record<string, SectionPresentation> = {};
  for (const [id, value] of Object.entries(presentation.sections)) {
    if (liveSectionIds.has(id)) {
      sections[id] = value;
    } else {
      changed = true;
    }
  }
  return changed ? { ...presentation, sections } : presentation;
}
